import { useCallback, useEffect, useRef, useState } from 'react';
import { useSafeTimeout } from './useSafeTimeout';
import { useRetainedRef, type RetainedToken } from './retainedState';

export type ScrollRestoration = {
  /** 스크롤 정지 시점(onMomentumEnd/onEndDrag)에 현재 offset 저장. 워클릿에서 scheduleOnRN 으로 호출 가능. */
  save: (offset: number) => void;
  /** 리스트의 onContentSizeChange 에 연결. 콘텐츠가 자라 target 에 도달하면 1회 복원. */
  onContentSizeChange: (width: number, contentHeight: number) => void;
  /** 복원 위치가 잡힐 때까지 true — 리스트를 숨겨(opacity:0) "top 보였다 점프" 깜빡임 제거. */
  restoring: boolean;
};

/**
 * 동적 높이 리스트의 스크롤 위치를 visit(retainedState) 단위로 저장/복원한다.
 * 가려진 화면 unmount→remount(같은 visit)를 가로질러 위치를 보존한다.
 *
 * - 저장: save(offset) — 정지 시점에만(매 프레임 X)
 * - 복원: onContentSizeChange 로 콘텐츠가 target 까지 자라면 scrollTo 1회. 못 닿거나 로딩이
 *   늦으면 maxWaitMs 후 도달 최대치로 클램프하며 노출(무한 블랭크 방지).
 * - restoring 동안 리스트를 숨겨 깜빡임 제거. 새 visit(저장값 0)은 복원/숨김 하지 않음.
 *
 * token 은 호출부 모듈 스코프에 `retainedState(0)` 로 선언해 넘긴다.
 */
export const useScrollRestoration = (params: {
  token: RetainedToken<number>;
  /** offset 으로 스크롤(보통 listRef.scrollToOffset 래핑). 복원은 animated=false 로 호출된다. */
  scrollTo: (offset: number, animated: boolean) => void;
  /** 리스트 뷰포트 높이 — contentHeight 가 target+viewport 이상이어야 target 도달 가능. */
  viewportHeight: number;
  maxWaitMs?: number;
}): ScrollRestoration => {
  const { token, scrollTo, viewportHeight, maxWaitMs = 500 } = params;
  const setSafeTimeout = useSafeTimeout();
  const savedOffset = useRetainedRef(token);

  // 리렌더 0 — visit 동안 동일 retained 슬롯에 기록.
  const save = useCallback(
    (offset: number) => {
      savedOffset.current = offset;
    },
    [savedOffset],
  );

  const restoredRef = useRef(false);
  // mount 시점 스냅샷(per-mount): 이번 mount 가 복원할 목표. 이후 save 는 다음 visit 용.
  const targetRef = useRef(savedOffset.current);
  const [restoring, setRestoring] = useState(targetRef.current > 0);

  const finish = useCallback(() => {
    restoredRef.current = true;
    setRestoring(false);
  }, []);

  const onContentSizeChange = useCallback(
    (_width: number, contentHeight: number) => {
      if (restoredRef.current) return;
      const target = targetRef.current;
      if (target <= 0) {
        finish();
        return;
      }
      if (contentHeight >= target + viewportHeight) {
        scrollTo(target, false);
        finish();
      }
    },
    [viewportHeight, scrollTo, finish],
  );

  // 콘텐츠가 예전보다 짧거나 로딩이 늦어 target 에 못 닿는 경우의 backstop.
  useEffect(() => {
    if (targetRef.current <= 0) return;
    setSafeTimeout(() => {
      if (restoredRef.current) return;
      scrollTo(targetRef.current, false);
      finish();
    }, maxWaitMs);
  }, [setSafeTimeout, scrollTo, finish, maxWaitMs]);

  return { save, onContentSizeChange, restoring };
};
